import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface ImageUploaderProps {
  fotos: string[];
  onChange: (fotos: string[]) => void;
  maxFotos?: number;
  error?: string;
}

interface UploadingFile {
  id: string;
  name: string;
  preview: string;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  errorMsg?: string;
}

const ImageUploader = ({ fotos, onChange, maxFotos = 5, error }: ImageUploaderProps) => {
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(async (file: File, uploadId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${session.user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    // Simular progreso mientras sube
    const progressInterval = setInterval(() => {
      setUploading(prev =>
        prev.map(u =>
          u.id === uploadId && u.progress < 85
            ? { ...u, progress: u.progress + 15 }
            : u
        )
      );
    }, 200);

    const { data, error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });

    clearInterval(progressInterval);

    if (uploadError) {
      setUploading(prev =>
        prev.map(u =>
          u.id === uploadId
            ? { ...u, progress: 0, status: 'error', errorMsg: uploadError.message }
            : u
        )
      );
      return null;
    }

    const { data: urlData } = supabase.storage
      .from('product-images')
      .getPublicUrl(data.path);

    setUploading(prev =>
      prev.map(u =>
        u.id === uploadId ? { ...u, progress: 100, status: 'done' } : u
      )
    );

    // Remover de la lista de uploading después de 1.5s
    setTimeout(() => {
      setUploading(prev => prev.filter(u => u.id !== uploadId));
    }, 1500);

    return urlData.publicUrl;
  }, []);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const currentTotal = fotos.length + uploading.filter(u => u.status !== 'error').length;
    const available = maxFotos - currentTotal;
    if (available <= 0) return;

    const filesToProcess = Array.from(files).slice(0, available);
    const validFiles = filesToProcess.filter(f => f.type.startsWith('image/'));

    if (validFiles.length === 0) return;

    const newUploading: UploadingFile[] = validFiles.map(f => ({
      id: `${Date.now()}-${Math.random()}`,
      name: f.name,
      preview: URL.createObjectURL(f),
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploading(prev => [...prev, ...newUploading]);

    const uploadPromises = validFiles.map((file, i) =>
      uploadFile(file, newUploading[i].id)
    );

    const results = await Promise.all(uploadPromises);
    const newUrls = results.filter((url): url is string => url !== null);

    if (newUrls.length > 0) {
      onChange([...fotos, ...newUrls]);
    }
  }, [fotos, uploading, maxFotos, onChange, uploadFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleRemove = (index: number) => {
    const newFotos = fotos.filter((_, i) => i !== index);
    onChange(newFotos);
  };

  const totalCount = fotos.length + uploading.filter(u => u.status !== 'error').length;
  const canAddMore = totalCount < maxFotos;

  return (
    <div className="space-y-3">
      {/* Zona de drop / botón de upload */}
      {canAddMore && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
            dragOver
              ? 'border-gray-900 bg-gray-50'
              : error
              ? 'border-red-300 bg-red-50/30 hover:border-red-400'
              : 'border-gray-200 bg-gray-50/50 hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-image-add-line text-xl text-gray-500"></i>
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Arrastra fotos aquí o haz clic para seleccionar
          </p>
          <p className="text-xs text-gray-400">
            JPG, PNG, WEBP · Máx. 5MB por foto · {maxFotos - totalCount} foto{maxFotos - totalCount !== 1 ? 's' : ''} restante{maxFotos - totalCount !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Grid de fotos subidas + en progreso */}
      {(fotos.length > 0 || uploading.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {/* Fotos ya subidas */}
          {fotos.map((url, index) => (
            <div key={url} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200">
              <img
                src={url}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover object-top"
              />
              {index === 0 && (
                <span className="absolute top-1 left-1 bg-gray-900/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap">
                  Principal
                </span>
              )}
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}

          {/* Fotos en proceso de subida */}
          {uploading.map((item) => (
            <div key={item.id} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
              <img
                src={item.preview}
                alt={item.name}
                className="w-full h-full object-cover object-top opacity-50"
              />
              {item.status === 'uploading' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
                  {/* Spinner */}
                  <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  {/* Barra de progreso */}
                  <div className="w-3/4 bg-white/30 rounded-full h-1.5">
                    <div
                      className="bg-white rounded-full h-1.5 transition-all duration-300"
                      style={{ width: `${item.progress}%` }}
                    ></div>
                  </div>
                  <span className="text-white text-[10px] mt-1 font-semibold">{item.progress}%</span>
                </div>
              )}
              {item.status === 'done' && (
                <div className="absolute inset-0 flex items-center justify-center bg-green-500/40">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                </div>
              )}
              {item.status === 'error' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/40 p-1">
                  <i className="ri-error-warning-line text-white text-lg"></i>
                  <span className="text-white text-[9px] text-center mt-1 leading-tight">Error al subir</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-red-500 text-xs">{error}</p>}

      {/* Tip */}
      {fotos.length > 0 && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <i className="ri-information-line"></i>
          La primera foto será la imagen principal del artículo
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
